import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const VNPayReturn = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [paymentResult, setPaymentResult] = useState(null);

    useEffect(() => {
        // Parse payment result from URL parameters
        const success = searchParams.get('success') === 'true';
        const message = searchParams.get('message') || '';
        const amount = searchParams.get('amount') || '0';
        const transactionNo = searchParams.get('transactionNo') || '';
        const responseCode = searchParams.get('responseCode') || '';

        setPaymentResult({
            success,
            message,
            amount: parseInt(amount),
            transactionNo,
            responseCode
        });
    }, [searchParams]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
        }).format(amount);
    };

    const handleBackToDashboard = () => {
        navigate('/dashboard');
    };

    if (!paymentResult) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
            <div className="w-full max-w-lg">
                <div className="rounded-2xl bg-white p-8 shadow-2xl border border-slate-200">
                    {paymentResult.success ? (
                        <>
                            {/* Success Icon */}
                            <div className="flex justify-center mb-6">
                                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100 border-4 border-green-200">
                                    <i className="fas fa-check text-4xl text-green-600"></i>
                                </div>
                            </div>

                            {/* Success Message */}
                            <h2 className="text-2xl font-bold text-center text-slate-900 mb-2">
                                Thanh toán thành công!
                            </h2>
                            <p className="text-center text-slate-600 mb-6">
                                Giao dịch của bạn đã được xử lý thành công
                            </p>

                            {/* Transaction Details */}
                            <div className="space-y-4 mb-6">
                                <div className="rounded-xl bg-green-50 p-4 border border-green-200">
                                    <div className="text-sm text-green-700 mb-1">Số tiền đã thanh toán</div>
                                    <div className="text-2xl font-bold text-green-900">
                                        {formatCurrency(paymentResult.amount)}
                                    </div>
                                </div>

                                {paymentResult.transactionNo && (
                                    <div className="rounded-lg bg-slate-50 p-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-slate-600">Mã giao dịch</span>
                                            <span className="font-mono text-sm font-semibold text-slate-900">
                                                {paymentResult.transactionNo}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                <div className="rounded-lg bg-slate-50 p-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-600">Phương thức</span>
                                        <span className="font-semibold text-slate-900">VNPay</span>
                                    </div>
                                </div>

                                <div className="rounded-lg bg-slate-50 p-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-slate-600">Thời gian</span>
                                        <span className="font-semibold text-slate-900">
                                            {new Date().toLocaleString('vi-VN')}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Success Info */}
                            <div className="rounded-lg bg-blue-50 p-4 border border-blue-200 mb-6">
                                <div className="flex gap-2">
                                    <i className="fas fa-info-circle text-blue-600 mt-0.5"></i>
                                    <div className="text-sm text-blue-800">
                                        Học phí đã được cập nhật tự động vào hệ thống. Bạn có thể kiểm tra trạng thái trong trang Dashboard.
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Error Icon */}
                            <div className="flex justify-center mb-6">
                                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-red-100 border-4 border-red-200">
                                    <i className="fas fa-times text-4xl text-red-600"></i>
                                </div>
                            </div>

                            {/* Error Message */}
                            <h2 className="text-2xl font-bold text-center text-slate-900 mb-2">
                                Thanh toán không thành công
                            </h2>
                            <p className="text-center text-slate-600 mb-6">
                                {paymentResult.message || 'Giao dịch không được hoàn thành'}
                            </p>

                            {/* Error Details */}
                            {paymentResult.responseCode && (
                                <div className="rounded-lg bg-red-50 p-4 border border-red-200 mb-6">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm text-red-700">Mã lỗi</span>
                                        <span className="font-mono text-sm font-semibold text-red-900">
                                            {paymentResult.responseCode}
                                        </span>
                                    </div>
                                    <div className="text-sm text-red-700">
                                        {paymentResult.message}
                                    </div>
                                </div>
                            )}

                            {/* Help Info */}
                            <div className="rounded-lg bg-yellow-50 p-4 border border-yellow-200 mb-6">
                                <div className="flex gap-2">
                                    <i className="fas fa-exclamation-triangle text-yellow-600 mt-0.5"></i>
                                    <div className="text-sm text-yellow-800">
                                        <strong>Lưu ý:</strong> Nếu số tiền đã bị trừ khỏi tài khoản nhưng giao dịch thất bại, vui lòng liên hệ bộ phận hỗ trợ với mã giao dịch để được xử lý.
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                        <button
                            onClick={handleBackToDashboard}
                            className="flex-1 rounded-lg bg-indigo-600 px-6 py-3 font-semibold text-white transition hover:bg-indigo-700"
                        >
                            <i className="fas fa-home mr-2"></i>
                            Về trang chủ
                        </button>
                        {!paymentResult.success && (
                            <button
                                onClick={() => window.history.back()}
                                className="flex-1 rounded-lg border-2 border-slate-300 bg-white px-6 py-3 font-semibold text-slate-700 transition hover:bg-slate-50"
                            >
                                <i className="fas fa-redo mr-2"></i>
                                Thử lại
                            </button>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="text-center mt-6">
                    <p className="text-sm text-slate-500">
                        Nếu cần hỗ trợ, vui lòng liên hệ admin
                    </p>
                </div>
            </div>
        </div>
    );
};

export default VNPayReturn;
